import { BiObjectsVerticalBottom, BiCalendarEvent } from "react-icons/bi";
import { BiAnalyse } from "react-icons/bi";
import { BsFiles } from "react-icons/bs";
import { GoProjectTemplate } from "react-icons/go";
import {
  GrInProgress,
  GrOrganization,
  GrGroup,
  GrConfigure,
  GrAlert,
  GrUnorderedList,
  GrDocumentConfig,
} from "react-icons/gr";
import { MdOutlineCases } from "react-icons/md";
import { MdOutlineSettingsInputComponent } from "react-icons/md";
import { MdContentPasteSearch, MdOutlineDataset, MdShare } from "react-icons/md";
import { RiGroupLine } from "react-icons/ri";
import { RxTokens } from "react-icons/rx";
import { TbSettings } from "react-icons/tb";
import { TbPlugConnected, TbReportAnalytics } from "react-icons/tb";
import { VscTypeHierarchySub } from "react-icons/vsc";

import { getAnalyticsUrl, getManagerUrl } from "@/config";

import { roleAdmin, roleRead, roleWrite } from "../../shared/constants/user-roles";
import {
  IOnBoardingPlatform,
  IOnBoardingAnalytics,
  IOnBoardingManager
} from "../../shared/context/auth-context";
import { IDropdownTab, ISideBarConfig, ITab } from "../components/side-bar/side-bar";
import { FaSearch } from "react-icons/fa";

export const createSideBarConfig = (
  roles: string[],
  hasSystemAdminRole: boolean,
  onboardingAnalyticsData: IOnBoardingAnalytics,
  onboardingPlatformData: IOnBoardingPlatform,
  onboardingManagerData: IOnBoardingManager
): ISideBarConfig => {
  const hasManager = getManagerUrl() !== "";
  const hasAnalytics = getAnalyticsUrl() !== "";
  const hasTenantAdminRole = roles.indexOf(roleAdmin) !== -1;
  const hasReadRole = roles.indexOf(roleRead) !== -1;
  const hasWriteRole = roles.indexOf(roleWrite) !== -1;

  const technicalConfigPaths = () => {
    const result = [
      {
        project: "platform",
        link: "/run-mode-configuration",
        displayName: "Run mode config",
        icon: <GrConfigure />,
        amount: onboardingAnalyticsData.runmodeconfig,
        allowed: hasAnalytics && hasTenantAdminRole
      },
      {
        project: "analytics",
        link: "/analytics/transaction-monitoring-system-config",
        displayName: "TMS config",
        icon: <MdOutlineSettingsInputComponent />,
        amount: onboardingAnalyticsData.tmsconfig,
        allowed: hasAnalytics && hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/database-connection",
        displayName: "Database connection",
        icon: <TbPlugConnected />,
        amount: onboardingPlatformData.database_connection,
        allowed: hasAnalytics && hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/tokens",
        displayName: "Tokens",
        icon: <RxTokens />,
        amount: onboardingPlatformData.token,
        allowed: hasTenantAdminRole
      },
      {
        project: "manager",
        link: "/manager/configuration",
        displayName: "Manager configuration",
        icon: <GrConfigure />,
        amount: undefined,
        allowed: hasManager && hasTenantAdminRole
      },
    ];

    const activeTabs = result.filter((tab) => tab.allowed);
    if (activeTabs.length === 0) {
      return undefined;
    }

    return {
      title: "Technical config",
      tabs: activeTabs,
    };
  };

  const datamodelPaths = () => {
    const result = [
      {
        project: "platform",
        link: "/entities",
        displayName: "Entity types",
        icon: <BiObjectsVerticalBottom />,
        amount: onboardingPlatformData.entity,
        allowed: hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/events",
        displayName: "Event types",
        icon: <BiCalendarEvent />,
        amount: onboardingPlatformData.event,
        allowed: hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/alertcasetypes",
        displayName: "AlertCase types",
        icon: <GrAlert />,
        amount: onboardingPlatformData.alertcase,
        allowed: hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/lists",
        displayName: "Lists",
        icon: <GrUnorderedList />,
        amount: onboardingPlatformData.enumeration,
        allowed: hasTenantAdminRole
      },
      {
        project: "analytics",
        link: "/data-monitoring",
        displayName: "Data monitoring",
        icon: <BiAnalyse />,
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole
      },
      {
        project: "platform",
        link: "/datamodel-graph",
        displayName: "Datamodel graph",
        icon: <MdShare />,
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole
      },
      {
        project: "platform",
        link: "/datamodel-reports",
        displayName: "Datamodel reports",
        icon: <TbReportAnalytics />,
        amount: onboardingPlatformData.datamodel_report,
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole
      },
      {
        project: "platform",
        link: "/datamodel-reports/configurations",
        displayName: "Report configurations",
        icon: <GrDocumentConfig />,
        amount: onboardingPlatformData.datamodel_report,
        allowed: hasTenantAdminRole || hasWriteRole
      }
    ];

    const activeTabs = result.filter((tab) => tab.allowed);
    if (activeTabs.length === 0) {
      return undefined;
    }

    return {
      title: "Datamodels",
      tabs: activeTabs,
    };
  };

  const analyticsPaths = () => {
    if (!hasAnalytics) {
      return undefined;
    }

    const result = [
      {
        project: "analytics",
        link: "/analytics/datasets",
        displayName: "Datasets",
        icon: <MdOutlineDataset />,
        amount: onboardingAnalyticsData.dataset,
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole
      },
      {
        project: "analytics",
        link: "/analytics/run-status",
        displayName: "Model run status",
        icon: <GrInProgress />,
        amount: undefined,
        allowed: hasTenantAdminRole || hasWriteRole || hasReadRole
      },
      {
        project: "analytics",
        link: "/analytics/admin/templates",
        displayName: "Model report templates",
        icon: <GoProjectTemplate />,
        amount: onboardingAnalyticsData.tenant_template,
        allowed: hasTenantAdminRole
      },
    ];

    const activeTabs = result.filter((tab) => tab.allowed);
    if (activeTabs.length === 0) {
      return undefined;
    }

    return {
      title: "Analytics",
      tabs: activeTabs,
    };
  };

  const systemAdminTenantAdminPaths = () => {
    const result = [
      {
        project: "platform",
        link: "/tenants",
        displayName: "Tenants",
        icon: <GrOrganization />,
        amount: undefined,
        allowed: hasSystemAdminRole || hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/users",
        displayName: "Users",
        icon: <RiGroupLine />,
        amount: undefined,
        allowed: hasSystemAdminRole || hasTenantAdminRole
      },
      {
        project: "platform",
        link: "/user-groups",
        displayName: "User groups",
        icon: <GrGroup />,
        amount: undefined,
        allowed: hasManager && (hasSystemAdminRole || hasTenantAdminRole)
      },
      {
        project: "analytics",
        link: "/templates",
        displayName: "Global report templates",
        icon: <GoProjectTemplate />,
        amount: onboardingAnalyticsData.template,
        allowed: hasAnalytics && (hasSystemAdminRole || hasTenantAdminRole)
      },
      {
        project: "manager",
        link: "/manager/deleted-alertcases",
        displayName: "Deleted alertcases",
        icon: <MdOutlineCases />,
        amount: undefined,
        allowed: hasTenantAdminRole
      }
    ];

    const activeTabs = result.filter((tab) => tab.allowed);
    if (activeTabs.length === 0) {
      return undefined;
    }

    return {
      title: "Admin",
      tabs: activeTabs,
    };
  };

  const managerPaths = () => {
    if (!hasManager) {
      return undefined;
    }
    const result = [
      {
        project: "manager",
        link: "/manager/alertcases",
        displayName: "AlertCases",
        icon: <MdOutlineCases />,
        amount: undefined,
        allowed: hasReadRole || hasWriteRole
      },
      {
        project: "manager",
        link: "/manager/document-search",
        displayName: "Document search",
        icon: <MdContentPasteSearch />,
        amount: undefined,
        allowed: hasReadRole || hasWriteRole
      },
      {
        project: "manager",
        link: "/manager/advanced-search",
        displayName: "Advanced search",
        icon: <FaSearch />,
        amount: undefined,
        allowed: hasReadRole || hasWriteRole
      },
      {
        project: "manager",
        link: "/manager/screens",
        displayName: "Screens",
        icon: <BsFiles />,
        amount: onboardingManagerData.screen,
        allowed: hasTenantAdminRole
      },
      {
        project: "manager",
        link: "/manager/entity_capture_screens",
        displayName: "Entity capture screens",
        icon: <BiObjectsVerticalBottom />,
        amount: onboardingManagerData.entity_capture,
        allowed: hasTenantAdminRole
      },
      {
        project: "manager",
        link: "/manager/event_capture_screens",
        displayName: "Event capture screens",
        icon: <BiCalendarEvent />,
        amount: onboardingManagerData.event_capture,
        allowed: hasTenantAdminRole
      },
      {
        project: "manager",
        link: "/manager/actions",
        displayName: "Actions",
        icon: <TbSettings />,
        amount: onboardingManagerData.action,
        allowed: hasTenantAdminRole
      },
      {
        project: "manager",
        link: "/manager/domain-process-types",
        displayName: "Domain/Process types",
        icon: <VscTypeHierarchySub />,
        amount: onboardingManagerData.domain_type,
        allowed: hasTenantAdminRole
      },
    ];

    const activeTabs = result.filter((tab) => tab.allowed);
    if (activeTabs.length === 0) {
      return undefined;
    }

    return {
      title: "Manager",
      tabs: activeTabs,
    };
  };

  const tabs: (ITab | IDropdownTab)[] = [
    datamodelPaths() as IDropdownTab,
    analyticsPaths() as IDropdownTab,
    managerPaths() as IDropdownTab,
    technicalConfigPaths() as IDropdownTab,
    systemAdminTenantAdminPaths() as IDropdownTab,
  ].filter((tab) => tab !== undefined);

  return {
    tabs: tabs
  };
};
